import type {
  IAnnotationData,
  ICreateAnnotationRequest,
  IModifyAnnotationRequest,
  IRemoveAnnotationRequest,
  IAnnotationPhotoData,
  IAnnotationPhotoUploadData,
  IReadAnnotationPageRequest,
  IReadAnnotationPhotoPageRequest,
  ICreateAnnotationPhotoRequest,
  ICreateProjectRequest,
  IModifyProjectRequest,
  ICreateWorkRequest,
  IProjectData,
  IPlanData,
  IWorkData,
  ISourceData,
  ISourceUploadData,
  IModifyWorkRequest,
  IRemoveWorkRequest,
  IReadWorkPageRequest,
  IReadMyWorkPageRequest,
  IUploadSourceRequest,
  ICreatePlanRequest,
  IUploadPlanRequest,
  IRemovePlanRequest,
  IModifyPlanRequest,
  IReadProjectPageRequest,
  IReadSourcePageRequest,
} from '@datatype/gridgio/api-type';
import axios from '@config/axios-config';
import { IApiResponse } from '@datatype/common/api-type';

export const readProjectPage = async (request: IReadProjectPageRequest): Promise<IApiResponse<IProjectData[]>> => {
  const { page, perPage } = request;
  const query = { page, perPage };
  const endpoint = '/api/v1/gi/project';
  const response = await axios.get<IApiResponse<IProjectData[]>>(endpoint, { params: query }).then((res) => res.data);
  return response;
};

export const readProject = async (projectKey: string): Promise<IApiResponse<IProjectData>> => {
  const endpoint = `/api/v1/gi/project/${projectKey}`;
  const response = await axios.get<IApiResponse<IProjectData>>(endpoint).then((res) => res.data);
  return response;
};

export const createProject = async (request: ICreateProjectRequest): Promise<IApiResponse<IProjectData>> => {
  const { name, description, place, address, coordinate } = request;
  const body = { name, description, place, address, coordinate };
  const endpoint = '/api/v1/gi/project';
  const response = await axios.post<IApiResponse<IProjectData>>(endpoint, body).then((res) => res.data);
  return response;
};

export const readBookmarkProjectPage = async (
  request: IReadProjectPageRequest,
): Promise<IApiResponse<IProjectData[]>> => {
  const { page, perPage } = request;
  return axios
    .get<IApiResponse<IProjectData[]>>('/api/v1/gi/project/bookmark', { params: { page, perPage } })
    .then((res) => res.data);
};

export const modifyProject = async (request: IModifyProjectRequest): Promise<IApiResponse<IProjectData>> => {
  const { projectKey, ...body } = request;
  return axios.patch<IApiResponse<IProjectData>>(`/api/v1/gi/project/${projectKey}`, body).then((res) => res.data);
};

export const removeProject = async (projectKey: string): Promise<IApiResponse<void>> =>
  axios.delete<IApiResponse<void>>(`/api/v1/gi/project/${projectKey}`).then((res) => res.data);

export const readWorkPage = async (request: IReadWorkPageRequest): Promise<IApiResponse<IWorkData[]>> => {
  const { projectKey, page, perPage, sortBy, sortOrder } = request;
  const query = { page, perPage, sortBy, sortOrder };
  const endpoint = `/api/v1/gi/project/${projectKey}/work`;
  const response = await axios.get<IApiResponse<IWorkData[]>>(endpoint, { params: query }).then((res) => res.data);
  return response;
};

export const readMyWorkPage = async (request: IReadMyWorkPageRequest): Promise<IApiResponse<IWorkData[]>> => {
  const { page, perPage, sortBy, sortOrder } = request;
  const query = { page, perPage, sortBy, sortOrder };
  const endpoint = '/api/v1/gi/me/work';
  const response = await axios.get<IApiResponse<IWorkData[]>>(endpoint, { params: query }).then((res) => res.data);
  return response;
};

export const readWork = async (workKey: string): Promise<IApiResponse<IWorkData>> => {
  const endpoint = `/api/v1/gi/work/${workKey}`;
  const response = await axios.get<IApiResponse<IWorkData>>(endpoint).then((res) => res.data);
  return response;
};

export const createWork = async (request: ICreateWorkRequest): Promise<IApiResponse<IWorkData>> => {
  const { projectKey, planKey, name, description, configuration } = request;
  const body = { planKey, name, description, configuration };
  const endpoint = `/api/v1/gi/project/${projectKey}/work`;
  const response = await axios.post<IApiResponse<IWorkData>>(endpoint, body).then((res) => res.data);
  return response;
};

export const modifyWork = async (request: IModifyWorkRequest): Promise<IApiResponse<IWorkData>> => {
  const { workKey, ...body } = request;
  const endpoint = `/api/v1/gi/work/${workKey}`;
  const response = await axios.patch<IApiResponse<IWorkData>>(endpoint, body).then((res) => res.data);
  return response;
};

export const removeWork = async (request: IRemoveWorkRequest): Promise<IApiResponse<void>> => {
  const endpoint = `/api/v1/gi/work/${request.workKey}`;
  return axios.delete<IApiResponse<void>>(endpoint).then((res) => res.data);
};

export const uploadSource = async (request: IUploadSourceRequest): Promise<IApiResponse<ISourceData>> => {
  const { name, description, source } = request;
  const body = {
    name,
    description,
    filename: source.name,
    contentType: source.type,
    filesize: source.size,
  };
  const createEndpoint = '/api/v1/gi/source';
  const sourceResponse = await axios.post<IApiResponse<ISourceData>>(createEndpoint, body).then((res) => res.data);
  const sourceKey = sourceResponse.data.key;
  const uploadEndpoint = `/api/v1/gi/source/${sourceKey}/upload`;
  const upload = await axios.post<IApiResponse<ISourceUploadData>>(uploadEndpoint).then((res) => res.data);
  const uploadResponse = await fetch(upload.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': source.type },
    body: source,
  });
  if (!uploadResponse.ok) throw new Error('source upload failed');

  const checkEndpoint = `/api/v1/gi/source/${sourceKey}/upload-check`;
  return axios.post<IApiResponse<ISourceData>>(checkEndpoint).then((res) => res.data);
};

export const readSourcePage = async (request: IReadSourcePageRequest): Promise<IApiResponse<ISourceData[]>> => {
  const response = await axios.get<IApiResponse<ISourceData[]>>('/api/v1/gi/source', { params: request });
  return response.data;
};

export const readAnnotationPage = async (
  request: IReadAnnotationPageRequest,
): Promise<IApiResponse<IAnnotationData[]>> => {
  const { workKey, page, perPage } = request;
  const query = { page, perPage, sortBy: 'createdAt', sortOrder: 'ASC' };
  const endpoint = `/api/v1/gi/work/${workKey}/annotation`;
  const response = await axios
    .get<IApiResponse<IAnnotationData[]>>(endpoint, { params: query })
    .then((res) => res.data);
  return response;
};

// 도면에는 모든 손상이 필요하므로 서버 페이지를 끝까지 읽은 뒤 한 번에 표시한다.
export const readAnnotations = async (workKey: string): Promise<IApiResponse<IAnnotationData[]>> => {
  const response = await readAnnotationPage({ workKey, page: 1, perPage: 100 });
  const data = [...response.data];
  for (let page = 2; page <= (response.totalPage ?? 1); page += 1) {
    const next = await readAnnotationPage({ workKey, page, perPage: 100 });
    data.push(...next.data);
  }
  return { status: response.status, code: response.code, message: response.message, data };
};

export const createAnnotation = async (request: ICreateAnnotationRequest): Promise<IApiResponse<IAnnotationData>> => {
  const { workKey, sheetId, issueId, mark } = request;
  const body = { sheetId, issueId, mark };
  const endpoint = `/api/v1/gi/work/${workKey}/annotation`;
  const response = await axios.post<IApiResponse<IAnnotationData>>(endpoint, body).then((res) => res.data);
  return response;
};

export const modifyAnnotation = async (request: IModifyAnnotationRequest): Promise<IApiResponse<IAnnotationData>> => {
  const { annotationKey, property, memo } = request;
  const body = { property, memo };
  const endpoint = `/api/v1/gi/annotation/${annotationKey}`;
  const response = await axios.patch<IApiResponse<IAnnotationData>>(endpoint, body).then((res) => res.data);
  return response;
};

export const removeAnnotation = async (request: IRemoveAnnotationRequest): Promise<IApiResponse<IAnnotationData>> => {
  const { annotationKey } = request;
  const endpoint = `/api/v1/gi/annotation/${annotationKey}`;
  const response = await axios.delete<IApiResponse<IAnnotationData>>(endpoint).then((res) => res.data);
  return response;
};

export const readAnnotationPhotoPage = async (
  request: IReadAnnotationPhotoPageRequest,
): Promise<IApiResponse<IAnnotationPhotoData[]>> => {
  const { annotationKey, page, perPage } = request;
  const query = { page, perPage, sortBy: 'createdAt', sortOrder: 'ASC' };
  const endpoint = `/api/v1/gi/annotation/${annotationKey}/photo`;
  const response = await axios
    .get<IApiResponse<IAnnotationPhotoData[]>>(endpoint, { params: query })
    .then((res) => res.data);
  return response;
};

// 사진 개수와 썸네일이 첫 페이지에 한정되지 않도록 모든 페이지를 읽는다.
export const readAnnotationPhotos = async (annotationKey: string): Promise<IApiResponse<IAnnotationPhotoData[]>> => {
  const response = await readAnnotationPhotoPage({ annotationKey, page: 1, perPage: 100 });
  const data = [...response.data];
  for (let page = 2; page <= (response.totalPage ?? 1); page += 1) {
    const next = await readAnnotationPhotoPage({ annotationKey, page, perPage: 100 });
    data.push(...next.data);
  }
  return { status: response.status, code: response.code, message: response.message, data };
};

export const createAnnotationPhoto = async (
  request: ICreateAnnotationPhotoRequest,
): Promise<IApiResponse<IAnnotationPhotoData>> => {
  const { annotationKey, filename, contentType, filesize } = request;
  const body = { filename, contentType, filesize };
  const endpoint = `/api/v1/gi/annotation/${annotationKey}/photo`;
  const response = await axios.post<IApiResponse<IAnnotationPhotoData>>(endpoint, body).then((res) => res.data);
  return response;
};

export const createAnnotationPhotoUpload = async (
  photoKey: string,
): Promise<IApiResponse<IAnnotationPhotoUploadData>> => {
  const endpoint = `/api/v1/gi/annotation-photo/${photoKey}/upload`;
  const response = await axios.post<IApiResponse<IAnnotationPhotoUploadData>>(endpoint).then((res) => res.data);
  return response;
};

export const checkAnnotationPhotoUpload = async (photoKey: string): Promise<IApiResponse<IAnnotationPhotoData>> => {
  const endpoint = `/api/v1/gi/annotation-photo/${photoKey}/upload-check`;
  const response = await axios.post<IApiResponse<IAnnotationPhotoData>>(endpoint).then((res) => res.data);
  return response;
};

export const removeAnnotationPhoto = async (photoKey: string): Promise<IApiResponse<IAnnotationPhotoData>> => {
  const endpoint = `/api/v1/gi/annotation-photo/${photoKey}`;
  const response = await axios.delete<IApiResponse<IAnnotationPhotoData>>(endpoint).then((res) => res.data);
  return response;
};

export const createPlan = async (request: ICreatePlanRequest): Promise<IApiResponse<IPlanData>> => {
  const { projectKey, ...body } = request;
  const endpoint = `/api/v1/gi/project/${projectKey}/plan`;
  return axios.post<IApiResponse<IPlanData>>(endpoint, body).then((res) => res.data);
};

export const uploadPlan = async (request: IUploadPlanRequest): Promise<IApiResponse<ISourceData>> => {
  const { sourceKey, source } = request;
  const uploadEndpoint = `/api/v1/gi/source/${sourceKey}/upload`;
  const upload = await axios.post<IApiResponse<ISourceUploadData>>(uploadEndpoint).then((res) => res.data);
  const uploadResponse = await fetch(upload.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': source.type },
    body: source,
  });
  if (!uploadResponse.ok) throw new Error('plan upload failed');
  const checkEndpoint = `/api/v1/gi/source/${sourceKey}/upload-check`;
  return axios.post<IApiResponse<ISourceData>>(checkEndpoint).then((res) => res.data);
};

export const removePlan = async (request: IRemovePlanRequest): Promise<IApiResponse<void>> => {
  const { projectKey, planKey } = request;
  return axios.delete<IApiResponse<void>>(`/api/v1/gi/project/${projectKey}/plan/${planKey}`).then((res) => res.data);
};

export const modifyPlan = async (request: IModifyPlanRequest): Promise<IApiResponse<IPlanData>> => {
  const { projectKey, planKey, name, description } = request;
  const endpoint = `/api/v1/gi/project/${projectKey}/plan/${planKey}`;
  const response = await axios.patch<IApiResponse<IPlanData>>(endpoint, { name, description }).then((res) => res.data);
  return response;
};
